// Facts behind the report lede and lens (DESIGN_SPEC §12.10). Pure: events + record in, facts out.
// It derives only what the sentences need (processes, drops, persistence, injections, YARA, the
// busiest non-background host, uploads), straight from the events, so the lede does not wait on
// the figure derivations and its tests stay deterministic.
//   deriveFacts(input) → ReportFacts
//   variantOf(stream, job)        the page variant from the EFFECTIVE state (never the record)
import { classifyNoise } from "../../lib/derive/noise";
import { envPath, winBase, winDir } from "../../lib/format";
import { configOf } from "./derive/config";
import { countsAsInjection } from "./derive/tree";
import { displayOf, phaseOf, type DisplayState } from "./lifecycle";
import type { Facts, JobStream, NoiseIndex, Transition, VerdictSnap } from "@/lib/derive/types";
import type { EffectiveState, Event, Job, Node, Tag } from "@/types";

type Persistence = "Run key" | "scheduled task" | "service" | "Startup folder" | "IFEO debugger";
type Clause = { phrase: string; sev: number };

export type ReportFacts = Facts & {
  kind: "file" | "url";
  name: string;
  /** Display phase (dispatched → provisioning). */
  phaseDisplay: DisplayState;
  /** The record names a node or a dispatch time (false: cancelled or still waiting before dispatch). */
  dispatched: boolean;
  /** An in-scope process started. */
  sampleStarted: boolean;
  /** Process, file and registry events (any scope). */
  activity: number;
  /** Epoch ms the run stopped (terminal), else null. */
  endedAt: number | null;
  /** The stop is known only from the record; atT is then the last event the stream delivered. */
  stopFromRecord: boolean;
  /** Epoch ms the current phase began (live), T0 while running. */
  since: number | null;
  /** T0 + timeout while running, else null. */
  deadline: number | null;
  /** Epoch ms the job was submitted (record), else null. */
  submittedAt: number | null;
  timeoutMs: number;
  t0Source: "running" | "booting" | null;
  /** Clauses from high and critical tags the event-derived clauses do not cover. */
  extra: Clause[];
  /** What the config extractors found (config.extracted events): families, C2, findings. */
  config: { families: string[]; c2: string[]; findings: number };
  /** Severity behind each event-derived clause (from the citing tags; defaults when unscored). */
  sev: { drop: number; persist: number; inject: number };
  tagCount: number;
  maxTagSev: number;
  strongest?: { title: string; sev: number };
  topPath?: string;
  queue?: QueueFacts;
};

type QueueFacts = {
  position: number;
  profile: string;
  offered: { id: string; free: number; max: number }[];
};

export type FactsInput = {
  job: Job;
  events: readonly Event[];
  state: EffectiveState;
  transitions: readonly Transition[];
  t0: number | null;
  t0Source: "running" | "booting" | null;
  verdict: VerdictSnap | null;
  /** Authoritative tags (behavior-report.json after the end), else the verdict's. */
  tags?: readonly Tag[];
  noise?: NoiseIndex;
  /** The jobs list (queue position) and the fleet (slot summary), for queued runs. */
  jobs?: readonly Job[];
  nodes?: readonly Node[];
};

const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));
const num = (v: unknown): number => {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() ? Number(v) : NaN;
  return Number.isFinite(n) ? n : NaN;
};
const lower = (s: string) => s.trim().toLowerCase().replace(/\//g, "\\");
const hostOnly = (h: string) => h.trim().toLowerCase().replace(/\.$/, "").replace(/:\d+$/, "");

const UPLOAD_MIN = 16 * 1024;

/** Clause phrases for rules that have no event-derived clause (high and critical only). */
const RULE_PHRASES: Record<string, string> = {
  "defense-evasion.defender-disable": "tampered with Windows Defender",
  "execution.lolbin-powershell": "ran an encoded PowerShell command",
  "execution.lolbin-rundll32": "ran rundll32 on a non-system target",
  "execution.lolbin-regsvr32": "ran regsvr32 against a remote scriptlet",
  "execution.lolbin-mshta": "ran a remote script through mshta",
  "execution.lolbin-certutil": "used certutil to download or decode",
  "execution.lolbin-bitsadmin": "queued a bitsadmin download",
  "execution.lolbin-wmic": "ran wmic",
  "execution.office-child-proc": "had an Office app start a script engine",
  "defense-evasion.firewall-disable": "turned off Windows Firewall",
  "defense-evasion.clear-eventlog": "cleared Windows event logs",
  "defense-evasion.uac-disable": "weakened UAC",
  "defense-evasion.masquerade-system-binary": "posed as a system binary",
  "impact.shadow-copy-delete": "deleted volume shadow copies",
  "impact.ransom-note": "wrote a ransom note",
  "impact.mass-file-write": "rewrote user files en masse",
  "credential-access.lsass-access": "opened LSASS memory",
  "credential-access.cloud-metadata": "queried the cloud metadata endpoint",
  "collection.browser-data": "read browser credential stores",
  "c2.tor-onion": "resolved a .onion address",
  "c2.dga-like-domain": "resolved DGA-like domain names",
};

/** Rules whose meaning is carried by an event-derived clause or by the YARA sentence. */
const COVERED: Record<string, "drop" | "persist" | "inject" | "yara" | "net"> = {
  "execution.dropped-then-exec": "drop",
  "payload.dropped-executable": "drop",
  "persistence.autorun-registry": "persist",
  "persistence.scheduled-task": "persist",
  "persistence.service-install": "persist",
  "persistence.startup-folder": "persist",
  "persistence.ifeo-debugger": "persist",
  "injection.remote-thread": "inject",
  "signature.yara-match": "yara",
  "network.http-outbound": "net",
  "network.dns-lookup": "net",
};

const PERSIST_FROM_TAG: Record<string, Persistence> = {
  "persistence.autorun-registry": "Run key",
  "persistence.scheduled-task": "scheduled task",
  "persistence.service-install": "service",
  "persistence.startup-folder": "Startup folder",
  "persistence.ifeo-debugger": "IFEO debugger",
};
const PERSIST_ORDER: readonly Persistence[] = ["Run key", "scheduled task", "service", "Startup folder", "IFEO debugger"];

function median(xs: number[]): number | undefined {
  if (!xs.length) return undefined;
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

/** Median inter-arrival in seconds (one decimal) when there are at least three contacts. */
function cadence(times: number[]): number | undefined {
  if (times.length < 3) return undefined;
  const s = [...times].sort((a, b) => a - b);
  const gaps: number[] = [];
  for (let i = 1; i < s.length; i++) gaps.push(s[i] - s[i - 1]);
  const m = median(gaps);
  return m === undefined ? undefined : Math.round(m / 100) / 10;
}

type HostAgg = { host: string; count: number; methods: Map<string, number>; paths: Set<string>; times: number[] };

export function deriveFacts(input: FactsInput): ReportFacts {
  const { job, events, state, transitions, t0, t0Source, verdict } = input;
  const tags = input.tags ?? verdict?.tags ?? [];
  const noise = input.noise ?? classifyNoise(events);

  // ---- processes -------------------------------------------------------------------------
  const scoped = new Set<number>();
  const procName = new Map<number, string>();
  let processCount = 0;
  let activity = 0;
  const written = new Map<string, number>(); // lower path → writer pid
  const dropExec: Facts["dropExec"] = [];
  const injectedInto: Facts["injectedInto"] = [];
  const injectedPids = new Set<number>();
  const yara: Facts["yara"] = [];
  const persist = new Set<Persistence>();
  let rootPid: number | null = null;

  for (const e of events) {
    const f = e.fields ?? {};
    const k = e.kind;
    if (k.startsWith("proc.") || k === "image.load" || k.startsWith("file.") || k.startsWith("registry.")) activity++;
    switch (k) {
      case "proc.start": {
        const pid = num(f.pid);
        const image = str(f.image);
        const inScope = f.scope !== false;
        if (Number.isFinite(pid)) procName.set(pid, winBase(image) || image);
        if (inScope && Number.isFinite(pid)) {
          scoped.add(pid);
          processCount++;
          if (rootPid === null) rootPid = pid;
          const drop = written.get(lower(image));
          if (drop !== undefined && pid !== rootPid && !dropExec.some((d) => d.pid === pid)) {
            dropExec.push({ name: winBase(image), dir: envPath(winDir(image)), pid });
          }
          const cmd = str(f.cmdline);
          if (/\bschtasks(\.exe)?["']?\s.*\/create\b/i.test(cmd)) persist.add("scheduled task");
          if (/\bsc(\.exe)?["']?\s+create\b/i.test(cmd)) persist.add("service");
        }
        break;
      }
      case "file.create":
      case "file.write":
      case "file.rename": {
        const path = str(k === "file.rename" ? f.to : f.path);
        const pid = num(f.pid);
        if (path && !/[\\/]$/.test(path)) written.set(lower(path), pid);
        if (/\\start menu\\programs\\startup\\/i.test(path)) persist.add("Startup folder");
        break;
      }
      case "registry.set": {
        const key = str(f.key);
        if (/\\currentversion\\run(once)?(\\|$)/i.test(key)) persist.add("Run key");
        else if (/\\image file execution options\\.+\\debugger$/i.test(key)) persist.add("IFEO debugger");
        else if (/\\system\\currentcontrolset\\services\\/i.test(key) && /\\imagepath$/i.test(key)) persist.add("service");
        break;
      }
      case "thread.inject": {
        const target = num(f.target_pid);
        const creator = num(f.creator_pid);
        if (!Number.isFinite(target) || !countsAsInjection(creator, target) || injectedPids.has(target)) break;
        injectedPids.add(target);
        const image = str(f.target_image);
        injectedInto.push({ name: procName.get(target) ?? (winBase(image) || `pid ${target}`), pid: target });
        break;
      }
      case "yara.match": {
        const artifact = str(f.artifact);
        const kind = str(f.kind);
        yara.push({ rule: str(f.rule), artifact, onDump: kind === "process-dump" || kind === "memory-dump" || /\.dmp$/i.test(artifact) });
        break;
      }
    }
  }
  for (const t of tags) {
    const p = PERSIST_FROM_TAG[t.id];
    if (p) persist.add(p);
  }
  const persistence = PERSIST_ORDER.filter((p) => persist.has(p));

  // ---- network (non-background only) ------------------------------------------------------
  const http = new Map<string, HostAgg>();
  const uploadsBy = new Map<string, number>();
  const conns = new Map<string, HostAgg>();
  const hosts = new Set<string>();
  const names = new Set<string>();
  const namesOf = (ip: string) => (noise.ipNames.get(ip) ?? []).filter((n) => !noise.bgNames.has(n));
  const contact = (dst: string) => {
    const ip = dst.replace(/^\[|\]$/g, "").replace(/:\d+$/, "");
    const ns = namesOf(ip);
    if (ns.length) ns.forEach((n) => hosts.add(n));
    else if (ip) hosts.add(ip);
    return ns[0] ?? ip;
  };
  for (const e of events) {
    if (!e.kind.startsWith("net.") || noise.isBackground(e)) continue;
    const f = e.fields ?? {};
    const ts = Date.parse(e.ts);
    if (e.kind === "net.dns") {
      const n = hostOnly(str(f.qname ?? f.name));
      if (n) names.add(n);
    } else if (e.kind === "net.http") {
      const host = hostOnly(str(f.host));
      if (!host) continue;
      hosts.add(host);
      const method = str(f.method).toUpperCase() || "GET";
      const req = num(f.req_bytes);
      if ((method === "POST" || method === "PUT") && req >= UPLOAD_MIN) {
        uploadsBy.set(host, (uploadsBy.get(host) ?? 0) + req);
        continue;
      }
      const agg: HostAgg = http.get(host) ?? { host, count: 0, methods: new Map(), paths: new Set(), times: [] };
      agg.count++;
      agg.methods.set(method, (agg.methods.get(method) ?? 0) + 1);
      agg.paths.add(str(f.path) || "/");
      if (Number.isFinite(ts)) agg.times.push(ts);
      http.set(host, agg);
    } else if (e.kind === "net.conn" || e.kind === "net.flow") {
      const dst = str(f.dst);
      if (!dst) continue;
      const host = contact(dst);
      if (e.kind === "net.conn") {
        const agg: HostAgg = conns.get(host) ?? { host, count: 0, methods: new Map(), paths: new Set(), times: [] };
        agg.count++;
        if (Number.isFinite(ts)) agg.times.push(ts);
        conns.set(host, agg);
      }
    }
  }
  const busiest = (m: Map<string, HostAgg>) => [...m.values()].sort((a, b) => b.count - a.count || (a.times[0] ?? 0) - (b.times[0] ?? 0))[0];
  let topHost: Facts["topHost"];
  let topPath: string | undefined;
  const topHttp = busiest(http);
  if (topHttp) {
    const method = [...topHttp.methods.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
    topHost = { host: topHttp.host, count: topHttp.count, method, cadenceSec: cadence(topHttp.times) };
    if (topHttp.paths.size === 1) topPath = [...topHttp.paths][0];
  } else {
    const topConn = busiest(conns);
    if (topConn) topHost = { host: topConn.host, count: topConn.count, cadenceSec: cadence(topConn.times) };
  }
  const uploads = [...uploadsBy.entries()].map(([host, bytes]) => ({ host, bytes })).sort((a, b) => b.bytes - a.bytes);

  // ---- tags -------------------------------------------------------------------------------
  const sevOf = (cover: "drop" | "persist" | "inject", dflt: number) => {
    const s = tags.filter((t) => COVERED[t.id] === cover).map((t) => t.severity);
    return s.length ? Math.max(...s) : dflt;
  };
  const extra: Clause[] = [];
  for (const t of [...tags].sort((a, b) => b.severity - a.severity)) {
    if (t.severity < 4 || COVERED[t.id]) continue;
    const phrase = RULE_PHRASES[t.id];
    if (phrase && !extra.some((c) => c.phrase === phrase)) extra.push({ phrase, sev: t.severity });
  }
  const maxTagSev = tags.reduce((m, t) => Math.max(m, t.severity), 0);
  const strongestTag = [...tags].sort((a, b) => b.severity - a.severity || b.hits - a.hits)[0];

  // ---- lifecycle --------------------------------------------------------------------------
  const terminal = [...transitions].reverse().find((t) => t.state === "failed" || t.state === "cancelled" || t.state === "completed");
  const recEnd = job.ended_at ? Date.parse(job.ended_at) : NaN;
  const endedAt = terminal && Number.isFinite(terminal.ts) ? terminal.ts : Number.isFinite(recEnd) ? recEnd : null;
  // a stop only the record knows (the stream never delivered it): the run was last seen at the last event
  const lastTs = events.length ? Date.parse(events[events.length - 1].ts) : NaN;
  const stopFromRecord = terminal?.source === "record" && terminal.state !== "completed" && events.length > 0;
  const stopAt = stopFromRecord && Number.isFinite(lastTs) && (endedAt === null || lastTs < endedAt) ? lastTs : endedAt;
  const phaseDisplay = phaseOf(transitions, state);
  const live = !["completed", "failed", "cancelled"].includes(state);
  const own = displayOf(state);
  const cur = live ? transitions.find((t) => displayOf(t.state) === own) : undefined;
  const since = live ? (t0Source === "running" && t0 !== null ? t0 : cur && Number.isFinite(cur.ts) ? cur.ts : null) : null;
  const timeoutMs = (job.timeout_secs || 0) * 1000;
  const deadline = live && t0Source === "running" && t0 !== null && timeoutMs > 0 ? t0 + timeoutMs : null;
  const reason = (terminal && terminal.state !== "completed" ? terminal.reason : undefined) || job.reason || undefined;

  return {
    state,
    phase: phaseDisplay,
    phaseDisplay,
    atT: stopAt !== null && t0 !== null && !live ? stopAt - t0 : null,
    stopFromRecord,
    reason,
    node: job.node_id,
    profile: job.profile,
    kind: job.kind === "url" ? "url" : "file",
    name: job.kind === "url" ? job.url ?? "" : job.filename ?? job.sample_sha ?? job.id,
    dispatched: !!job.node_id || !!job.dispatched_at,
    sampleStarted: scoped.size > 0,
    activity,
    yara,
    config: (({ families, c2, findings }) => ({ families, c2, findings: findings.length }))(configOf(undefined, events)),
    dropExec,
    persistence,
    injectedInto,
    topHost,
    topPath,
    uploads,
    hostCount: hosts.size,
    nameCount: names.size,
    processCount,
    eventCount: events.length,
    networkCount: noise.network,
    backgroundCount: noise.total,
    tagsAboveInfo: tags.filter((t) => t.severity > 1).length,
    tagCount: tags.length,
    maxTagSev,
    strongest: strongestTag ? { title: strongestTag.title, sev: strongestTag.severity } : undefined,
    extra,
    sev: { drop: sevOf("drop", 5), persist: sevOf("persist", 4), inject: sevOf("inject", 4) },
    endedAt,
    since,
    deadline,
    submittedAt: Number.isFinite(Date.parse(job.submitted_at)) ? Date.parse(job.submitted_at) : null,
    timeoutMs,
    t0Source,
    queue: state === "queued" ? queueFacts(job, input.jobs, input.nodes) : undefined,
  };
}

function queueFacts(job: Job, jobs?: readonly Job[], nodes?: readonly Node[]): QueueFacts | undefined {
  if (!jobs && !nodes) return undefined;
  const mine = Date.parse(job.submitted_at);
  const ahead = (jobs ?? []).filter(
    (j) => j.id !== job.id && j.state === "queued" && j.profile === job.profile && Date.parse(j.submitted_at) < mine,
  ).length;
  const offered = (nodes ?? [])
    .filter((n) => n.profiles?.includes(job.profile))
    .map((n) => ({ id: n.id, free: n.free_slots, max: n.max_slots }));
  return { position: ahead + 1, profile: job.profile, offered };
}

// ---- page variant and verdict (DESIGN_SPEC §11.4, §12.1) ------------------------------------

export type Variant = "queued" | "live" | "document" | "failed" | "cancelled-before";

export function variantOf(stream: Pick<JobStream, "state" | "snap">, job: Job): Variant {
  switch (stream.state) {
    case "queued":
      return "queued";
    case "completed":
      return "document";
    case "failed":
      return "failed";
    case "cancelled":
      return !job.node_id && !job.dispatched_at && stream.snap.events.length === 0 ? "cancelled-before" : "document";
    default:
      return "live";
  }
}
